import { X } from "lucide-react";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { ReactNode } from "react";

import {
  collectConsecutiveOutputMedia,
  hasFullscreenMediaContent,
  isOutputMediaContent,
} from "./FullscreenDialog.utils";
import CollapsibleMessageContent from "./CollapsibleMessageContent";
import { IMessageStore } from "./message-store";
import RoleSelector from "./RoleSelector";

import ContentArea from "@/components/ContentArea";
import { ExternalChangeProvider } from "@/components/ContentArea/external-update-context";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { MediaBlock, OutputMediaBlock } from "@/components/ui/MediaBlocks";
import PrettyJsonView from "@/components/ui/PrettyJsonView";
import { Media, MediaContent, TextContent } from "@/types";
import { getPrettifiableJsonText } from "@/utils/jsonDisplay";
import { detectMediaTypeFromUrl } from "@/utils/media";

const FullscreenTextBlock = ({
  text,
  readonly,
  showLineNumbers,
  enableJsonOutputDetection,
  isOutputMessageComplete,
  messageRole,
  onChange,
}: {
  text: string;
  readonly: boolean;
  showLineNumbers: boolean;
  enableJsonOutputDetection: boolean;
  isOutputMessageComplete: boolean;
  messageRole: string;
  onChange: (value: string) => void;
}) => {
  const parsedJson = getPrettifiableJsonText({
    enableJsonOutputDetection,
    isLiveToolCall: false,
    isOutputMessageComplete,
    messageRole,
    readonly,
    text,
  });

  if (parsedJson) {
    return (
      <div className="overflow-auto rounded border p-3">
        <PrettyJsonView
          data={parsedJson.data}
          rawText={parsedJson.source}
          className="h-fit min-h-full border-none bg-transparent px-0 py-0"
        />
      </div>
    );
  }

  return (
    <ContentArea
      onChange={event => onChange(event.target.value)}
      value={text}
      showLineNumbers={showLineNumbers}
      className="min-h-[200px] rounded border text-sm"
      readOnly={readonly}
      placeholder={readonly ? "" : "Type / to use a widget..."}
    />
  );
};

const FullscreenDialog = ({
  isOpen,
  onOpenChange,
  message,
  readonly = false,
  showLineNumbers = false,
  enableJsonOutputDetection = false,
  isOutputMessageComplete = false,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  message: IMessageStore;
  enableJsonOutputDetection?: boolean;
  readonly?: boolean;
  showLineNumbers?: boolean;
  isOutputMessageComplete?: boolean;
}) => {
  const blocks = message.contentBlocks;
  const contents = blocks.map(block => block.content);
  const hasMedia = hasFullscreenMediaContent(contents);

  const parsedJson = getPrettifiableJsonText({
    enableJsonOutputDetection,
    isLiveToolCall: false,
    isOutputMessageComplete,
    messageRole: message.role,
    readonly,
    text: message.fullTextContent,
  });

  const renderMixedContent = () => {
    const nodes: ReactNode[] = [];

    for (let index = 0; index < blocks.length; index += 1) {
      const { type, content, index: storeIndex } = blocks[index];

      if (type === "output_media") {
        const previous = contents[index - 1];
        if (previous && isOutputMediaContent(previous)) {
          continue;
        }

        const group = collectConsecutiveOutputMedia(contents, index);
        const first = group[0];
        if (!first) {
          continue;
        }

        nodes.push(
          <OutputMediaBlock
            key={`output-media-${storeIndex}`}
            content={first}
            extraImages={group.slice(1).filter(item => (item.media_type ?? "image") === "image")}
            readonly
          />,
        );
        continue;
      }

      if (type === "media") {
        const mediaContent = content as MediaContent;
        const rawMedia = mediaContent.media;
        const normalizedMedia: Media = {
          ...rawMedia,
          type: detectMediaTypeFromUrl(rawMedia.url, rawMedia.type),
        };
        nodes.push(
          <MediaBlock key={`media-${storeIndex}`} media={normalizedMedia} readonly={readonly} />,
        );
        continue;
      }

      if (type === "text") {
        const { text } = content as TextContent;
        if (readonly && !text) {
          continue;
        }
        nodes.push(
          <FullscreenTextBlock
            key={`text-${storeIndex}`}
            text={text}
            readonly={readonly}
            showLineNumbers={showLineNumbers}
            enableJsonOutputDetection={enableJsonOutputDetection}
            isOutputMessageComplete={isOutputMessageComplete}
            messageRole={message.role}
            onChange={value => message.setTextContentBlockAt(storeIndex, value)}
          />,
        );
      }
    }

    return nodes;
  };

  let body: ReactNode;
  if (hasMedia) {
    body = (
      <div className="h-full min-h-0 overflow-auto">
        <CollapsibleMessageContent maxCollapsedHeight={520} className="w-full">
          <div className="flex flex-col gap-4">{renderMixedContent()}</div>
        </CollapsibleMessageContent>
      </div>
    );
  } else if (parsedJson) {
    body = (
      <div className="h-full overflow-auto rounded border p-3">
        <PrettyJsonView
          data={parsedJson.data}
          rawText={parsedJson.source}
          className="h-fit min-h-full border-none bg-transparent px-0 py-0"
        />
      </div>
    );
  } else {
    body = (
      <ContentArea
        onChange={event => !readonly && message.setMessage(event.target.value)}
        value={message.content}
        showLineNumbers={showLineNumbers}
        className="h-full rounded border text-sm"
        containerClassName="h-full"
        readOnly={readonly}
        placeholder={readonly ? "" : "Type / to use a widget..."}
      />
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="fixed inset-0 left-0 top-0 flex h-screen w-screen max-w-none transform-none flex-col rounded-none border-0 p-0 shadow-none"
        shouldShowCloseButton={false}
        motion="none"
      >
        <div className="flex h-14 items-center justify-between gap-x-2 border-b bg-background px-4 py-3">
          <RoleSelector
            selected={message.role}
            readonly={readonly}
            onSelect={role => runInAction(() => message.setRole(role))}
          />

          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            className="h-8 w-8 rounded-full hover:bg-muted"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close fullscreen</span>
          </Button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto bg-background">
          <div className="px-4 pb-4 pt-4">
            <ExternalChangeProvider>{body}</ExternalChangeProvider>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default observer(FullscreenDialog);
